/**
 * AuthSignInPanel — shared sign-in hero used by every member-facing page
 * that gates its content behind Google sign-in (InstituteHub,
 * InstituteMyProgress, InstituteQuickStudy, InstituteFinalAssessment,
 * LeaderDashboard, MinistryHub, MyServingSchedule).
 *
 * Design rules (see /app/design_guidelines.md § 1):
 *   • Body content is centered via `mx-auto` inside a `max-w-*` block.
 *   • The dove/heart/cross emblem sits on the RIGHT as a decorative
 *     accent — never crowding the centered CTA. On mobile it fades to
 *     a soft background wash so the CTA stays the focal point.
 *   • Wrapper carries `data-dark-hero="true"` so the gold-brand tokens
 *     (`text-primary`, `.overline`) stay vibrant (see § 0).
 *
 * Props:
 *   - children   — headline / body / CTA block, will be centered.
 *   - testid     — optional data-testid for the wrapping section.
 *   - minHeight  — override for `min-h-*` (default: "min-h-[100svh]").
 *   - topPadding — top padding (default: "pt-32 md:pt-36" clears the nav).
 */
import React from "react";

const EMBLEM = "/generated/brand/logo-800.webp";

export default function AuthSignInPanel({
  children,
  testid,
  minHeight = "min-h-[100svh]",
  topPadding = "pt-32 md:pt-36",
}) {
  return (
    <section
      data-testid={testid}
      data-dark-hero="true"
      className={`${minHeight} ${topPadding} pb-20 px-5 md:px-10 relative overflow-hidden flex items-center justify-center`}
      style={{ backgroundColor: "#0F1E3D", color: "#F4EFE4" }}
    >
      {/* Decorative brand emblem — soft on mobile, accent on desktop.
          Sits behind the CTA (z-0) so the centered content stays the
          focal point on every viewport. The .brand-emblem-drift wrapper
          gently floats the emblem on the Y axis; the img itself keeps
          .brand-logo-shine so the periodic shimmer still fires. */}
      <div
        aria-hidden="true"
        className="brand-emblem-drift pointer-events-none absolute right-[-32%] sm:right-[-14%] md:right-[-2%] lg:right-[4%] top-1/2 -translate-y-1/2 w-[85%] sm:w-[60%] md:w-[40%] lg:w-[34%] max-w-[520px] opacity-[0.18] sm:opacity-25 md:opacity-60 lg:opacity-75 z-0"
      >
        <img
          src={EMBLEM}
          alt=""
          aria-hidden="true"
          draggable={false}
          className="w-full h-auto select-none brand-logo-shine"
          style={{ filter: "drop-shadow(0 12px 32px rgba(0,0,0,0.45))" }}
        />
      </div>
      <div className="relative z-10 w-full max-w-xl mx-auto text-center">
        {children}
      </div>
    </section>
  );
}
